import React, { useState, useMemo } from 'react';
import { Button, ComboBoxInput, TextBlock } from 'bench-internal-theme';
import { colors } from 'bench-style-constants';
import { useTaskOperations, TaskOperationState } from '../../hooks/useTaskOperations';
import {
  DataEntryWithTask,
  analyzeTaskCancellation,
  analyzeTaskCreation,
  getTaskOperationMessages,
} from './taskOperations';
import type { ComboBoxOptions } from 'bench-internal-theme';
import { TaskCreationFieldsLayout } from './TaskOperationsPanel.styles';
import { openModal } from 'modal-core-components';
import ApplyConfirmationModal from '../apply-confirmation-modal/ApplyConfirmationModal';
import { TASK_TYPES, TASK_TYPE_OPTIONS } from './constants';

interface TaskOperationsPanelProps {
  dataEntries: DataEntryWithTask[];
  clientId: string;
  personId: string;
  isFeatureEnabled: boolean;
  onTasksCreated?: () => void;
  onTasksCancelled?: () => void;
}

export const TaskOperationsPanel: React.FC<TaskOperationsPanelProps> = ({
  dataEntries,
  clientId,
  personId,
  isFeatureEnabled,
  onTasksCreated,
  onTasksCancelled,
}) => {
  const [taskType, setTaskType] = useState<string>(TASK_TYPES.REVIEW_CATEGORY);

  // Get the selected option object for the ComboBoxInput
  const selectedOption =
    TASK_TYPE_OPTIONS.find(option => option.value === taskType) || TASK_TYPE_OPTIONS[0];

  const { taskOperationState, createTasks, cancelTasks, canCancelTasks, canCreateTasks } =
    useTaskOperations();

  const handleCreateTasks = () => {
    const onConfirm = createTasks({
      taskType,
      dataEntries,
      clientId,
      personId,
      isFeatureEnabled,
    });

    const creationSummary = analyzeTaskCreation(dataEntries);
    const messages = getTaskOperationMessages('create', creationSummary);

    openModal(closeModal => (
      <ApplyConfirmationModal
        title="Create Tasks"
        description={messages.confirmation?.message}
        closeModal={() => {
          closeModal();
        }}
        onConfirm={() => {
          closeModal();
          onConfirm(() => {
            setTaskType(TASK_TYPES.REVIEW_CATEGORY);
            onTasksCreated?.();
          });
        }}
      />
    ));
  };

  const handleCancelTasks = () => {
    const onConfirm = cancelTasks({
      taskType,
      dataEntries,
      clientId,
      isFeatureEnabled,
    });

    const cancellationSummary = analyzeTaskCancellation(dataEntries);
    const messages = getTaskOperationMessages('cancel', cancellationSummary);

    openModal(closeModal => (
      <ApplyConfirmationModal
        title="Cancel Tasks"
        description={messages.confirmation?.message}
        closeModal={() => {
          closeModal();
        }}
        onConfirm={() => {
          closeModal();
          onConfirm(() => {
            setTaskType(TASK_TYPES.REVIEW_CATEGORY);
            onTasksCancelled?.();
          });
        }}
      />
    ));
  };

  const isOperationInProgress = taskOperationState !== TaskOperationState.IDLE;
  const isCreating = taskOperationState === TaskOperationState.CREATING;
  const isCancelling = taskOperationState === TaskOperationState.CANCELLING;
  const dataEntriesCount = dataEntries.length;

  // Get the correct count of cancellable tasks for progress display
  const cancellableTaskCount = useMemo(() => {
    const summary = analyzeTaskCancellation(dataEntries);
    return summary.totalCount;
  }, [dataEntries]);

  // Get the correct count of creatable tasks for progress display
  const creatableTaskCount = useMemo(() => {
    const summary = analyzeTaskCreation(dataEntries);
    return summary.totalCount;
  }, [dataEntries]);

  // Memoize canCancelTasks result to avoid unnecessary recalculations
  const canCancel = useMemo(
    () =>
      canCancelTasks({
        taskType,
        dataEntries,
        clientId,
        isFeatureEnabled,
      }),
    [taskType, dataEntries, clientId, isFeatureEnabled, canCancelTasks],
  );

  const canCreate = useMemo(
    () =>
      canCreateTasks({
        taskType,
        dataEntries,
        clientId,
        isFeatureEnabled,
        personId,
      }),
    [canCreateTasks, taskType, dataEntries, clientId, isFeatureEnabled, personId],
  );

  // Memoize button disabled states
  const isCreateDisabled = isOperationInProgress || dataEntriesCount === 0 || !canCreate;
  const isCancelDisabled = isOperationInProgress || dataEntriesCount === 0 || !canCancel;

  if (!isFeatureEnabled) {
    return null;
  }

  return (
    <TaskCreationFieldsLayout>
      <div>
        <TextBlock styleType="small" uppercase>
          Task Type
        </TextBlock>
        <ComboBoxInput
          value={selectedOption}
          options={TASK_TYPE_OPTIONS as ComboBoxOptions}
          onChange={(option: { value: string; label: string } | null) =>
            setTaskType(option?.value || TASK_TYPES.REVIEW_CATEGORY)
          }
          disabled={isOperationInProgress}
        />
        <TextBlock styleType="small" uppercase>
          &nbsp;
        </TextBlock>
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <Button
          type="button"
          styleType="primary"
          onClick={handleCreateTasks}
          disabled={isCreateDisabled}
        >
          {isCreating ? 'Creating' : 'Create'} Tasks
        </Button>
        <Button
          type="button"
          styleType="secondary"
          onClick={handleCancelTasks}
          disabled={isCancelDisabled}
        >
          {isCancelling ? 'Cancelling' : 'Cancel'} Tasks
        </Button>
      </div>
      {isCreating && (
        <TextBlock styleType="small" color={colors.smoke} style={{ marginTop: '8px' }}>
          Creating {creatableTaskCount} {creatableTaskCount === 1 ? 'task' : 'tasks'}...
        </TextBlock>
      )}
      {isCancelling && (
        <TextBlock styleType="small" color={colors.smoke} style={{ marginTop: '8px' }}>
          Cancelling {cancellableTaskCount} {cancellableTaskCount === 1 ? 'task' : 'tasks'}...
        </TextBlock>
      )}
    </TaskCreationFieldsLayout>
  );
};

export default TaskOperationsPanel;
